import BreadCrumb from "Components/Common/BreadCrumb";
import PageHeaderActions from "Components/Common/PageHeaderActions";
import Icon from "Components/Icons/Icon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardBody, Row, Col, Container } from "reactstrap";
import TableContainer from "Components/Common/TableContainer";
import { useLocation, useNavigate } from "react-router-dom";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { deletePresetQuoteTemplate, getPresetQuoteDetail } from "slices/thunks";
import { ErrorHandler } from "utils/toast";
import ReadMoreText from "Components/Common/ReadMoreText";
import { clearPresetQuoteDetail } from "slices/presetQuotes/reducer";

const PresetQuoteDetail = () => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dispatch = useAppDispatch();
  const { presetQuoteDetail, isLoading } = useAppSelector(
    (state) => state?.presetQuote,
  );
  const { state } = useLocation();

  //Preset Quote Detail Api Call
  useEffect(() => {
    const apiCall = async () => {
      try {
        await dispatch(
          getPresetQuoteDetail({ templateId: state?.templateId }),
        )?.unwrap();
      } catch (error) {
        ErrorHandler(error);
      }
    };
    apiCall();

    return () => {
      dispatch(clearPresetQuoteDetail());
    };
  }, [dispatch, state?.templateId]);

  const handleDeletePresetTemplate = useCallback(async () => {
    await dispatch(
      deletePresetQuoteTemplate({ templateId: presetQuoteDetail?.id! }),
    )?.unwrap();
    setShowDeleteModal(false);
    navigate(-1);
  }, [dispatch, navigate, presetQuoteDetail?.id]);

  const columns = useMemo(
    () => [
      {
        header: "ITEM NAME",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "CATEGORY",
        accessorKey: "category_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },

      {
        header: "SUBCATEGORY",
        accessorKey: "subcategory_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "QUANTITY",
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const { quantity, unit } = cell?.row?.original;

          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {quantity} {unit}
            </span>
          );
        },
      },
      {
        header: "PRICE/UNIT",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
      {
        header: "UNIT COST",
        accessorKey: "cost",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
      {
        header: "TOTAL",
        accessorKey: "total_price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <BreadCrumb title="Preset Quote Detail" pageTitle="Preset Quotes" />
        <Container fluid className="mt-4">
          <div className="gap-3 d-flex flex-column">
            <PageHeaderActions
              title={presetQuoteDetail?.name ?? ""}
              subtitle=""
              actions={[
                {
                  label: "Edit",
                  onClick: () => {
                    navigate("/manage-preset-quotes", {
                      state: { templateId: presetQuoteDetail?.id },
                    });
                  },
                  leftIcon: (
                    <Icon
                      name="Edit"
                      className="text-white fill-white stroke-white"
                      fill="currentColor"
                      stroke="currentColor"
                    />
                  ),
                },
                {
                  label: "Delete",
                  onClick: () => {
                    setShowDeleteModal(true);
                  },
                  leftIcon: (
                    <i className="ri-delete-bin-line me-1 align-bottom"></i>
                  ),
                },
              ]}
            />
            <Row>
              <Col xxl={12}>
                <Card className="br-10 bg-white">
                  <CardBody>
                    <h6 className="mb-3 fw-medium text-black2D  font-primary fs-16">
                      Job Description
                    </h6>
                    <ReadMoreText text={presetQuoteDetail?.description ?? ""} />
                  </CardBody>
                </Card>
              </Col>

              <Col lg={12}>
                <Card id="itemList" className="br-10 bg-white">
                  <CardBody className="pt-0">
                    <div>
                      <TableContainer
                        columns={columns}
                        data={presetQuoteDetail?.items ?? []}
                        isGlobalFilter={true}
                        SearchPlaceholder="Search here"
                        divClass="table-responsive table-card mb-2 bg-white"
                        tableClass="align-middle table-nowrap table-borderless"
                        thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
                        theadClass="table-light"
                        leftContent={
                          <p className="m-0 fw-medium  text-black2D  font-primary">
                            Items
                          </p>
                        }
                        showPagination={false}
                        searchAlign="right"
                        onSearch={() => {}}
                        isInitialLoading={isLoading}
                        customPageSize={presetQuoteDetail?.items?.length || 0}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
        <DeleteItemModal
          show={showDeleteModal}
          onCloseClick={() => {
            setShowDeleteModal(false);
          }}
          onDeleteClick={handleDeletePresetTemplate}
        />
      </div>
    </React.Fragment>
  );
};

export default PresetQuoteDetail;
